import React, { useRef, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { pushSupported, getPushState, enablePush, disablePush } from "../lib/push";
import { User, SignOut, CaretRight, ShieldStar, ShieldCheck, Scroll, Camera, BellRinging, BellSlash } from "@phosphor-icons/react";

const LANGS = [["nl", "Nederlands"], ["de", "Deutsch"], ["en", "English"]];

export default function MyProfile() {
  const { user, setUser, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pushState, setPushState] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => { getPushState().then(setPushState); }, []);
  if (!user) { navigate("/login"); return null; }
  const roleLabel = user.role === "company" ? t("role_company") : t("role_guard");
  const isGuard = user.role === "guard";

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState === "subscribed") {
        await disablePush();
        setPushState("default");
        toast.success("Meldingen uitgezet");
      } else {
        await enablePush();
        setPushState("subscribed");
        toast.success("Meldingen aan — je krijgt nu spoedklussen in de buurt");
      }
    } catch (e) {
      if (e.message === "denied") toast.error("Meldingen geblokkeerd. Sta ze toe in je browserinstellingen.");
      else if (e.message === "unsupported") toast.error("Je browser ondersteunt geen meldingen.");
      else toast.error("Kon meldingen niet instellen.");
      setPushState(await getPushState());
    }
    setPushBusy(false);
  };

  const handleAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/upload?kind=avatar`, fd);
      setUser({ ...user, avatar_path: r.data.path });
    } catch (err) { /* ignore */ }
    setUploading(false);
    e.target.value = "";
  };

  const Row = ({ testid, icon, label, onClick, danger }) => (
    <button data-testid={testid} onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 border-b border-white/5 transition-colors ${danger ? "text-[#E11D2A] hover:bg-[#E11D2A]/10" : "text-white hover:bg-white/5"}`}>
      {icon}<span className="flex-1 text-left text-sm font-medium">{label}</span>
      {!danger && <CaretRight size={16} className="text-zinc-500" />}
    </button>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#0A0A0A] pb-24" data-testid="my-profile-page">
      <div className="max-w-lg mx-auto px-5 py-8">
        <h1 className="font-head text-2xl font-bold text-white mb-6">{t("nav_profile")}</h1>

        <div className="bg-[#12161d] border border-white/10 rounded-2xl p-6 flex items-center gap-4">
          <button data-testid="profile-avatar-btn" onClick={() => fileRef.current?.click()}
            className="relative w-16 h-16 rounded-full overflow-hidden shrink-0 group" disabled={uploading}>
            {user.avatar_path ? (
              <img src={fileUrl(user.avatar_path)} alt="" className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#E11D2A]/15 flex items-center justify-center"><User size={30} weight="duotone" className="text-[#E11D2A]" /></div>
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera size={20} weight="fill" className="text-white" />
            </span>
          </button>
          <input ref={fileRef} data-testid="profile-avatar-input" type="file" accept="image/*" className="hidden" onChange={handleAvatar} />
          <div className="min-w-0">
            <div className="text-white font-bold text-lg truncate">{user.name}</div>
            <span className="inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-[#E11D2A]/15 text-[#E11D2A] border border-[#E11D2A]/30">{roleLabel}</span>
            {uploading && <div className="text-xs text-zinc-500 mt-1">Uploaden…</div>}
          </div>
        </div>

        <div className="mt-4 bg-[#12161d] border border-white/10 rounded-2xl overflow-hidden">
          <Info label={t("email")} value={user.email} />
          {user.company_name && <Info label={t("company_name")} value={user.company_name} />}
          {user.city && <Info label={t("city")} value={user.city} />}
        </div>

        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 px-1">{t("language")}</div>
          <div className="grid grid-cols-3 gap-2">
            {LANGS.map(([code, name]) => (
              <button key={code} data-testid={`lang-${code}`} onClick={() => setLang(code)}
                className={`rounded-md py-2.5 text-sm font-semibold border transition-colors ${lang === code ? "border-[#E11D2A] bg-[#E11D2A] text-white" : "border-white/15 text-zinc-300 hover:border-white/30"}`}>{name}</button>
            ))}
          </div>
        </div>

        {isGuard && (
          <div className="mt-4 bg-[#12161d] border border-white/10 rounded-2xl p-5" data-testid="push-section">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E11D2A]/15 flex items-center justify-center shrink-0">
                {pushState === "subscribed"
                  ? <BellRinging size={20} weight="fill" className="text-[#E11D2A]" />
                  : <BellSlash size={20} weight="regular" className="text-zinc-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white font-semibold text-sm">Spoedklus-meldingen</div>
                <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">
                  Ontvang een pushmelding zodra er een spoedklus bij jou in de buurt staat — ook als GuardLink dicht is.
                </p>
                {pushState === "unsupported" && <p className="text-xs text-amber-400 mt-2">Je browser ondersteunt geen meldingen.</p>}
                {pushState === "denied" && <p className="text-xs text-amber-400 mt-2">Meldingen zijn geblokkeerd. Sta ze toe in je browserinstellingen.</p>}
              </div>
            </div>
            {pushState !== "unsupported" && pushState !== "denied" && (
              <button data-testid="push-toggle-btn" onClick={togglePush} disabled={pushBusy}
                className={`mt-4 w-full rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
                  pushState === "subscribed"
                    ? "border border-white/15 text-zinc-300 hover:border-white/30"
                    : "bg-[#E11D2A] text-white hover:bg-[#c4151f]"}`}>
                {pushBusy ? "Even geduld…" : pushState === "subscribed" ? "Meldingen uitzetten" : "Meldingen inschakelen"}
              </button>
            )}
          </div>
        )}

        <div className="mt-4 bg-[#12161d] border border-white/10 rounded-2xl overflow-hidden">
          {user.is_admin && <Row testid="profile-admin" icon={<ShieldStar size={20} weight="duotone" className="text-[#E11D2A]" />} label={t("nav_admin")} onClick={() => navigate("/admin")} />}
          <Row testid="profile-privacy" icon={<ShieldCheck size={20} weight="duotone" className="text-zinc-400" />} label="Privacybeleid" onClick={() => navigate("/privacy")} />
          <Row testid="profile-terms" icon={<Scroll size={20} weight="duotone" className="text-zinc-400" />} label="Algemene voorwaarden" onClick={() => navigate("/terms")} />
          <Row testid="profile-logout" icon={<SignOut size={20} weight="bold" />} label={t("nav_logout")} onClick={() => { logout(); navigate("/"); }} danger />
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="px-4 py-3 border-b border-white/5 last:border-0">
    <div className="text-xs text-zinc-500">{label}</div>
    <div className="text-sm text-white mt-0.5 break-words">{value}</div>
  </div>
);
